import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { Themed, ThemeName } from "@components/atoms/Themed"
import { dateFormat } from "@utils/dateFormat"
import { cn } from "@utils/tw"
import { defineBlock, EditableText } from "eddev/blocks"
import { useEffect, useState } from "react"
import { usePageContext } from "../../contexts/PageContext"

export const meta: BlockMeta = {
  title: "Page Header Dynamic",
  icon: "block-default",
  tags: ["#page"],
}

export default defineBlock("content/page-header-dynamic", (props) => {
  const [hour, setHour] = useState<string>(dateFormat(new Date(), "H"))
  const setting = parseInt(hour) < 12 ? "morning" : parseInt(hour) < 18 ? "afternoon" : "evening"
  const themes = {
    morning: "blue",
    afternoon: "bark",
    evening: "navy",
  } satisfies {
    [key: string]: ThemeName
  }
  const [theme, setTheme] = useState<ThemeName>("none")
  const [started, setStarted] = useState(false)
  const { setTheme: setPageTheme } = usePageContext()

  useEffect(() => {
    const timeout = setTimeout(() => {
      setTheme(themes[setting])
      setPageTheme(themes[setting] as ThemeName)
      setStarted(true)
    }, 100)

    return () => clearTimeout(timeout)
  }, [])

  return (
    <Themed theme={theme} className="w-full pt-10 themed-bg transition-colors duration-[1200ms]">
      <div className="w-grid mx-auto flex flex-col py-10 items-start justify-center gap-7 themed-fg transition-colors duration-300">
        <EditableText
          as="h1"
          store="title"
          className="type-title-l md:type-title-xl text-balance"
          placeholder="Enter a title"
        />
        <EditableText as="h2" store="subtitle" className="type-label-s-caps" placeholder="Enter an optional subtitle" />

        <div className="w-full flex flex-col gap-10 mt-10">
          {(setting === "morning" || env.admin) && (
            <Themed
              theme={env.admin ? themes["morning"] : "none"}
              className="w-full flex flex-col gap-10 themed-bg themed-fg"
            >
              <EditableText
                as="p"
                store="description"
                className={cn(
                  "type-title-m w-full md:w-contentW mx-auto",
                  "opacity-0 transition-opacity duration-[1500ms] delay-[500ms]",
                  started && "opacity-100",
                )}
                placeholder="Right now, it’s morning, when alignment tends to break down in hybrid teams."
                defaultValue="Right now, it’s morning, when alignment tends to break down in hybrid teams."
              />
              <div className="w-full grid grid-cols-2 md:grid-cols-11 gap-5">
                <div
                  className={cn(
                    "col-span-1 md:col-span-3 flex flex-col gap-2",
                    "opacity-0 transition-opacity duration-[1000ms] delay-[900ms]",
                    started && "opacity-100",
                  )}
                >
                  <div className="w-full h-[35vw] md:h-[18vw] md:max-h-[280px] rounded-lg overflow-hidden bg-black">
                    {props.morningImage1?.mediaItemUrl && !props.morningVideo1?.mediaItemUrl && (
                      <ResponsiveImage
                        {...props.morningImage1}
                        className="w-full h-full object-cover object-center"
                        objectFit="cover"
                      />
                    )}
                    {!props.morningImage1?.mediaItemUrl && props.morningVideo1?.mediaItemUrl && (
                      <video
                        src={props.morningVideo1.mediaItemUrl}
                        className="w-full h-full object-cover object-center"
                        playsInline
                        autoPlay
                        muted
                        loop
                      />
                    )}
                  </div>
                  <EditableText
                    as="p"
                    store="captionMorning1"
                    className="type-label-m"
                    placeholder="We create flexible zones for fast syncs"
                    defaultValue="We create flexible zones for fast syncs"
                  />
                </div>
                <div
                  className={cn(
                    "col-span-1 md:col-span-3 flex flex-col gap-2",
                    "opacity-0 transition-opacity duration-[1000ms] delay-[950ms]",
                    started && "opacity-100",
                  )}
                >
                  <div className="w-full h-[35vw] md:h-[18vw] md:max-h-[280px] flex items-center p-7 bg-black rounded-xl overflow-hidden">
                    <EditableText
                      as="p"
                      store="captionMorning"
                      className="type-body-xl md:type-title-s text-white"
                      placeholder="What does your team need to start strong?"
                      defaultValue="What does your team need to start strong?"
                    />
                  </div>
                </div>
                <div
                  className={cn(
                    "col-span-1 md:col-span-2 flex flex-col gap-2",
                    "opacity-0 transition-opacity duration-[1000ms] delay-[1000ms]",
                    started && "opacity-100",
                  )}
                >
                  <div className="w-full h-[35vw] md:h-[18vw] md:max-h-[280px] bg-black rounded-full overflow-hidden">
                    {props.morningImage2?.mediaItemUrl && !props.morningVideo2?.mediaItemUrl && (
                      <ResponsiveImage
                        {...props.morningImage2}
                        className="w-full h-full object-cover object-center"
                        objectFit="cover"
                      />
                    )}
                    {!props.morningImage2?.mediaItemUrl && props.morningVideo2?.mediaItemUrl && (
                      <video
                        src={props.morningVideo2.mediaItemUrl}
                        className="w-full h-full object-cover object-center"
                        playsInline
                        autoPlay
                        muted
                        loop
                      />
                    )}
                  </div>
                </div>
                <div
                  className={cn(
                    "col-span-1 md:col-span-3 flex flex-col gap-2",
                    "opacity-0 transition-opacity duration-[1000ms] delay-[1050ms]",
                    started && "opacity-100",
                  )}
                >
                  <div className="w-full h-[35vw] md:h-[18vw] md:max-h-[280px] bg-black rounded-lg overflow-hidden">
                    {props.morningImage3?.mediaItemUrl && !props.morningVideo3?.mediaItemUrl && (
                      <ResponsiveImage
                        {...props.morningImage3}
                        className="w-full h-full object-cover object-center"
                        objectFit="cover"
                      />
                    )}
                    {!props.morningImage3?.mediaItemUrl && props.morningVideo3?.mediaItemUrl && (
                      <video
                        src={props.morningVideo3.mediaItemUrl}
                        className="w-full h-full object-cover object-center"
                        playsInline
                        autoPlay
                        muted
                        loop
                      />
                    )}
                  </div>
                  <EditableText
                    as="p"
                    store="captionMorning2"
                    className="type-label-m"
                    placeholder="A redesign for intuitive entry"
                    defaultValue="A redesign for intuitive entry"
                  />
                </div>
              </div>
            </Themed>
          )}

          {(setting === "afternoon" || env.admin) && (
            <Themed
              theme={env.admin ? themes["afternoon"] : "none"}
              className="w-full flex flex-col gap-10 themed-bg themed-fg"
            >
              <EditableText
                as="p"
                store="description"
                className={cn(
                  "type-title-m w-full md:w-contentW mx-auto",
                  "opacity-0 transition-opacity duration-[1500ms] delay-[500ms]",
                  started && "opacity-100",
                )}
                placeholder="Right now, it’s afternoon, where drop-offs happen in poorly-lit areas."
                defaultValue="Right now, it’s afternoon, where drop-offs happen in poorly-lit areas."
              />
              <div className="w-full grid grid-cols-2 md:grid-cols-11 gap-5">
                <div
                  className={cn(
                    "col-span-1 md:col-span-3 flex flex-col gap-2",
                    "opacity-0 transition-opacity duration-[1000ms] delay-[900ms]",
                    started && "opacity-100",
                  )}
                >
                  <div className="w-full h-[35vw] md:h-[18vw] md:max-h-[280px] bg-black rounded-xl overflow-hidden">
                    {props.afternoonImage1?.mediaItemUrl && !props.afternoonVideo1?.mediaItemUrl && (
                      <ResponsiveImage
                        {...props.afternoonImage1}
                        className="w-full h-full object-cover object-center"
                        objectFit="cover"
                      />
                    )}
                    {!props.afternoonImage1?.mediaItemUrl && props.afternoonVideo1?.mediaItemUrl && (
                      <video
                        src={props.afternoonVideo1.mediaItemUrl}
                        className="w-full h-full object-cover object-center"
                        playsInline
                        autoPlay
                        muted
                        loop
                      />
                    )}
                  </div>
                  <EditableText
                    as="p"
                    store="captionAfternoon1"
                    className="type-label-m"
                    placeholder="We create flexible zones for fast syncs"
                    defaultValue="We create flexible zones for fast syncs"
                  />
                </div>
                <div
                  className={cn(
                    "col-span-1 md:col-span-3 flex flex-col gap-2",
                    "opacity-0 transition-opacity duration-[1000ms] delay-[950ms]",
                    started && "opacity-100",
                  )}
                >
                  <div className="w-full h-[35vw] md:h-[18vw] md:max-h-[280px] flex items-center p-7 bg-black rounded-full overflow-hidden">
                    <EditableText
                      as="p"
                      store="captionAfternoon"
                      className="type-body-xl md:type-title-s text-white"
                      placeholder="Daylight reorientation fixes this."
                      defaultValue="Daylight reorientation fixes this."
                    />
                  </div>
                </div>
                <div
                  className={cn(
                    "col-span-1 md:col-span-3 flex flex-col gap-2",
                    "opacity-0 transition-opacity duration-[1000ms] delay-[1000ms]",
                    started && "opacity-100",
                  )}
                >
                  <div className="w-full h-[35vw] md:h-[18vw] md:max-h-[280px] bg-black rounded-lg overflow-hidden">
                    {props.afternoonImage2?.mediaItemUrl && !props.afternoonVideo2?.mediaItemUrl && (
                      <ResponsiveImage
                        {...props.afternoonImage2}
                        className="w-full h-full object-cover object-center"
                        objectFit="cover"
                      />
                    )}
                    {!props.afternoonImage2?.mediaItemUrl && props.afternoonVideo2?.mediaItemUrl && (
                      <video
                        src={props.afternoonVideo2.mediaItemUrl}
                        className="w-full h-full object-cover object-center"
                        playsInline
                        autoPlay
                        muted
                        loop
                      />
                    )}
                  </div>
                  <EditableText
                    as="p"
                    store="captionAfternoon2"
                    className="type-label-m"
                    placeholder="A redesign for intuitive entry"
                    defaultValue="A redesign for intuitive entry"
                  />
                </div>
                <div
                  className={cn(
                    "col-span-1 md:col-span-2 flex flex-col gap-2",
                    "opacity-0 transition-opacity duration-[1000ms] delay-[1050ms]",
                    started && "opacity-100",
                  )}
                >
                  <div className="w-full h-[35vw] md:h-[18vw] md:max-h-[280px] bg-black rounded-full overflow-hidden">
                    {props.afternoonImage3?.mediaItemUrl && !props.afternoonVideo3?.mediaItemUrl && (
                      <ResponsiveImage
                        {...props.afternoonImage3}
                        className="w-full h-full object-cover object-center"
                        objectFit="cover"
                      />
                    )}
                    {!props.afternoonImage3?.mediaItemUrl && props.afternoonVideo3?.mediaItemUrl && (
                      <video
                        src={props.afternoonVideo3.mediaItemUrl}
                        className="w-full h-full object-cover object-center"
                        playsInline
                        autoPlay
                        muted
                        loop
                      />
                    )}
                  </div>
                </div>
              </div>
            </Themed>
          )}

          {(setting === "evening" || env.admin) && (
            <Themed
              theme={env.admin ? themes["evening"] : "none"}
              className="w-full flex flex-col gap-10 themed-bg themed-fg !text-white"
            >
              <EditableText
                as="p"
                store="description"
                className={cn(
                  "type-title-m w-full md:w-contentW mx-auto",
                  "opacity-0 transition-opacity duration-[1500ms] delay-[500ms]",
                  started && "opacity-100",
                )}
                placeholder="Right now, it’s evening, and when the sun sets, so does productivity."
                defaultValue="Right now, it’s evening, and when the sun sets, so does productivity."
              />
              <div className="w-full grid grid-cols-2 md:grid-cols-11 gap-5">
                <div
                  className={cn(
                    "col-span-1 md:col-span-3 flex flex-col gap-2",
                    "opacity-0 transition-opacity duration-[1000ms] delay-[900ms]",
                    started && "opacity-100",
                  )}
                >
                  <div className="w-full h-[35vw] md:h-[18vw] md:max-h-[280px] bg-white rounded-full overflow-hidden">
                    {props.eveningImage1?.mediaItemUrl && !props.eveningVideo1?.mediaItemUrl && (
                      <ResponsiveImage
                        {...props.eveningImage1}
                        className="w-full h-full object-cover object-center"
                        objectFit="cover"
                      />
                    )}
                    {!props.eveningImage1?.mediaItemUrl && props.eveningVideo1?.mediaItemUrl && (
                      <video
                        src={props.eveningVideo1.mediaItemUrl}
                        className="w-full h-full object-cover object-center"
                        playsInline
                        autoPlay
                        muted
                        loop
                      />
                    )}
                  </div>
                  <EditableText
                    as="p"
                    store="captionEvening1"
                    className="type-label-m"
                    placeholder="We create flexible zones for fast syncs"
                    defaultValue="We create flexible zones for fast syncs"
                  />
                </div>
                <div
                  className={cn(
                    "col-span-1 md:col-span-3 flex flex-col gap-2",
                    "opacity-0 transition-opacity duration-[1000ms] delay-[950ms]",
                    started && "opacity-100",
                  )}
                >
                  <div className="w-full h-[35vw] md:h-[18vw] md:max-h-[280px] flex items-center p-7 bg-white rounded-lg overflow-hidden">
                    <EditableText
                      as="p"
                      store="captionEvening"
                      className="type-body-xl md:type-title-s !text-black"
                      placeholder="Design for closure, not burnout."
                      defaultValue="Design for closure, not burnout."
                    />
                  </div>
                </div>
                <div
                  className={cn(
                    "col-span-1 md:col-span-2 flex flex-col gap-2",
                    "opacity-0 transition-opacity duration-[1000ms] delay-[1000ms]",
                    started && "opacity-100",
                  )}
                >
                  <div className="w-full h-[35vw] md:h-[18vw] md:max-h-[280px] bg-white rounded-full overflow-hidden">
                    {props.eveningImage2?.mediaItemUrl && !props.eveningVideo2?.mediaItemUrl && (
                      <ResponsiveImage
                        {...props.eveningImage2}
                        className="w-full h-full object-cover object-center"
                        objectFit="cover"
                      />
                    )}
                    {!props.eveningImage2?.mediaItemUrl && props.eveningVideo2?.mediaItemUrl && (
                      <video
                        src={props.eveningVideo2.mediaItemUrl}
                        className="w-full h-full object-cover object-center"
                        playsInline
                        autoPlay
                        muted
                        loop
                      />
                    )}
                  </div>
                </div>
                <div
                  className={cn(
                    "col-span-1 md:col-span-3 flex flex-col gap-2",
                    "opacity-0 transition-opacity duration-[1000ms] delay-[1050ms]",
                    started && "opacity-100",
                  )}
                >
                  <div className="w-full h-[35vw] md:h-[18vw] md:max-h-[280px] bg-white rounded-lg overflow-hidden">
                    {props.eveningImage3?.mediaItemUrl && !props.eveningVideo3?.mediaItemUrl && (
                      <ResponsiveImage
                        {...props.eveningImage3}
                        className="w-full h-full object-cover object-center"
                        objectFit="cover"
                      />
                    )}
                    {!props.eveningImage3?.mediaItemUrl && props.eveningVideo3?.mediaItemUrl && (
                      <video
                        src={props.eveningVideo3.mediaItemUrl}
                        className="w-full h-full object-cover object-center"
                        playsInline
                        autoPlay
                        muted
                        loop
                      />
                    )}
                  </div>
                  <EditableText
                    as="p"
                    store="captionEvening2"
                    className="type-label-m"
                    placeholder="A redesign for intuitive entry"
                    defaultValue="A redesign for intuitive entry"
                  />
                </div>
              </div>
            </Themed>
          )}
        </div>
      </div>
    </Themed>
  )
})
